import React, { useMemo, useState } from "react";
import {
  RefreshCw, Play, Square as StopIcon, Trash2, Sparkles,
  TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp,
  Banknote, CreditCard, Monitor,
} from "lucide-react";

// Phase 25.14a — POS Live MICRO variant.
// Glance-first dashboard for micro-SMBs (barber shops, food trucks, single-
// chair cafés). Owners want one answer: "Did I make money today?" — followed
// by 2-3 watch-outs (dead windows, voids, vs same-day-last-week). Everything
// else (charts, SKUs, live tail) lives under a single "Full terminal" toggle.
// Activated when workspace.tier === "micro".

const PAYMENT_LABELS = {
  card_tap: "Tap", card_chip: "Chip", apple_pay: "Apple Pay",
  google_pay: "Google Pay", cash: "Cash",
};

function fmtMoney(cents) {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function hourLabel(h) {
  // hour may be ISO "2026-02-04T14:00:00" or just "14" or "14:00"
  if (h == null) return "";
  if (typeof h === "string" && h.length > 5) {
    const d = new Date(h);
    if (!isNaN(d)) return d.toLocaleTimeString("en-US", { hour: "numeric" });
  }
  const num = parseInt(String(h).slice(0, 2), 10);
  if (Number.isFinite(num)) {
    const ampm = num >= 12 ? "pm" : "am";
    const h12 = ((num + 11) % 12) + 1;
    return `${h12}${ampm}`;
  }
  return String(h);
}

export default function POSLiveMicro({
  today, hourly, recent, playbook, loading,
  hasAnyData, streaming, provider,
  refreshingAI, busy, onRefresh, onRefreshAI,
  onStartDemo, onStopDemo, onClearDemo, onExitMicro,
  unitLabel = "txns",     // "cuts" for barber, "covers" for restaurant
  unitSingular = "txn",
}) {
  const [showFull, setShowFull] = useState(false);

  const revenue = today?.revenue_cents ?? 0;
  const txnCount = today?.txn_count ?? 0;
  const avgTicket = today?.avg_ticket_cents ?? 0;
  const tips = today?.tip_cents ?? 0;
  const delta = playbook?.close_of_day?.delta_vs_baseline_pct;
  const projection = playbook?.close_of_day?.projection_cents;

  // Derive watch-outs from real signals only — no fake alerts.
  const watchouts = useMemo(() => {
    const out = [];
    // Quiet windows: any hour with 0 txns during operating window (rough: 9am-6pm)
    const todayHour = new Date().getHours();
    const quietHours = (hourly || [])
      .map(h => {
        const num = typeof h.hour === "string" && h.hour.length > 5
          ? new Date(h.hour).getHours()
          : parseInt(String(h.hour).slice(0, 2), 10);
        return { num, count: h.txn_count || 0, label: hourLabel(h.hour) };
      })
      .filter(h => Number.isFinite(h.num) && h.num >= 9 && h.num <= 18 && h.num < todayHour && h.count === 0);
    if (quietHours.length > 0) {
      const labels = quietHours.slice(0, 2).map(h => h.label).join(", ");
      out.push({
        tone: "warn",
        text: `Dead ${quietHours.length === 1 ? "hour" : "hours"}: ${labels}${quietHours.length > 2 ? "…" : ""}`,
        hint: "Consider a flash promo for the next dead window.",
      });
    }
    // vs same-DOW baseline
    if (delta != null && delta <= -15) {
      out.push({
        tone: "warn",
        text: `${delta}% behind same-day last week`,
        hint: "Closing pace projects below average.",
      });
    }
    // Voids / refunds — check recent for negative totals
    const voids = (recent || []).filter(t => (t.total_cents || 0) < 0);
    if (voids.length > 0) {
      out.push({
        tone: "alert",
        text: `${voids.length} void${voids.length > 1 ? "s" : ""} today`,
        hint: voids.length >= 3 ? "Worth a glance — unusually high." : "Check the live tail.",
      });
    }
    return out;
  }, [hourly, recent, delta]);

  const pulseLine = useMemo(() => {
    if (!playbook?.summary) return null;
    // Trim to a single sentence for the glance view
    const first = String(playbook.summary).split(/(?<=[.!?])\s+/)[0];
    return first.length > 180 ? first.slice(0, 177) + "…" : first;
  }, [playbook]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="pos-live-micro">
      {/* Sticky compact header */}
      <header className="sticky top-0 z-10 bg-black/95 backdrop-blur border-b border-zinc-900">
        <div className="px-4 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">// DARA · TODAY</span>
              {streaming && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676] font-mono text-[9px] uppercase tracking-widest">
                  <span className="w-1 h-1 rounded-full bg-[#00E676] animate-pulse" /> LIVE
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={onRefresh}
              disabled={loading}
              data-testid="micro-refresh"
              className="p-2.5 border border-zinc-800 hover:border-zinc-700"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            {onExitMicro && (
              <button
                onClick={onExitMicro}
                data-testid="micro-exit"
                className="p-2.5 border border-zinc-800 hover:border-zinc-700"
                aria-label="Switch to full terminal"
                title="Switch to full terminal view"
              >
                <Monitor className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="p-4 pb-24 space-y-4">
        {/* Empty state */}
        {!loading && !hasAnyData && (
          <div className="border border-zinc-900 bg-zinc-950/40 p-6 text-center">
            <div className="font-heading font-black text-xl tracking-tight">No takings yet today.</div>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              Start the demo to see what DARA looks like during a busy day.
            </p>
            <button
              onClick={onStartDemo}
              disabled={busy}
              data-testid="micro-demo-start"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 w-full"
            >
              <Play className="w-4 h-4" />
              START DEMO
            </button>
          </div>
        )}

        {hasAnyData && (
          <>
            {/* HERO — single biggest number on the screen */}
            <div className="border border-[#FFB000]/30 bg-[#FFB000]/[0.06] p-5" data-testid="micro-hero">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                  Today
                </div>
                {delta != null && (
                  <div
                    className={`inline-flex items-center gap-1 font-mono text-[11px] ${delta >= 0 ? "text-[#00E676]" : "text-[#FF6B47]"}`}
                    data-testid="micro-delta"
                  >
                    {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {delta > 0 ? "+" : ""}{delta}% vs last {new Date().toLocaleDateString("en-US", { weekday: "short" })}
                  </div>
                )}
              </div>
              <div
                className="font-heading font-black text-5xl sm:text-6xl tracking-tighter text-[#FFB000] mt-1 leading-none"
                data-testid="micro-revenue"
              >
                {fmtMoney(revenue)}
              </div>
              <div className="font-mono text-xs text-zinc-300 mt-3">
                <span className="text-white font-bold">{txnCount}</span> {txnCount === 1 ? unitSingular : unitLabel}
                {" · avg "}
                <span className="text-white font-bold">{fmtMoney(avgTicket)}</span>
                {tips > 0 && (
                  <>
                    {" · tips "}
                    <span className="text-[#00E676] font-bold">{fmtMoney(tips)}</span>
                  </>
                )}
              </div>
              {projection != null && projection > 0 && (
                <div className="font-mono text-[10px] text-zinc-500 mt-2">
                  Projected close: {fmtMoney(projection)}
                </div>
              )}
            </div>

            {/* DARA PULSE — one sentence, tap to expand */}
            {pulseLine && (
              <PulseCard
                line={pulseLine}
                full={playbook?.summary}
                onRefresh={onRefreshAI}
                refreshing={refreshingAI}
              />
            )}
            {!pulseLine && playbook == null && (
              <button
                onClick={onRefreshAI}
                disabled={refreshingAI}
                data-testid="micro-pulse-generate"
                className="w-full border border-zinc-800 hover:border-[#FFB000] px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-zinc-400 flex items-center justify-center gap-2"
              >
                <Sparkles className={`w-3.5 h-3.5 ${refreshingAI ? "animate-pulse" : ""}`} />
                {refreshingAI ? "Generating pulse…" : "Generate DARA pulse"}
              </button>
            )}

            {/* WATCH-OUTS — only render if there are real signals */}
            {watchouts.length > 0 && (
              <div className="space-y-2" data-testid="micro-watchouts">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  // Watch-outs
                </div>
                {watchouts.map((w, i) => (
                  <div
                    key={i}
                    data-testid={`micro-watchout-${i}`}
                    className={`border p-3 flex items-start gap-3 ${
                      w.tone === "alert"
                        ? "border-[#FF6B47]/40 bg-[#FF6B47]/[0.05]"
                        : "border-zinc-800 bg-zinc-950/40"
                    }`}
                  >
                    <AlertTriangle
                      className={`w-4 h-4 flex-shrink-0 mt-0.5 ${w.tone === "alert" ? "text-[#FF6B47]" : "text-[#FFB000]"}`}
                    />
                    <div className="min-w-0">
                      <div className="text-sm text-white leading-snug">{w.text}</div>
                      {w.hint && (
                        <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-snug">{w.hint}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Demo controls — minimal */}
            {provider?.is_demo && (
              <div className="flex items-center gap-2 pt-1">
                {streaming ? (
                  <button
                    onClick={onStopDemo}
                    disabled={busy}
                    data-testid="micro-demo-stop"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-[#FF6B47]/40 text-[#FF6B47] font-mono text-[11px] uppercase tracking-widest px-3 py-2.5"
                  >
                    <StopIcon className="w-4 h-4" /> Pause demo
                  </button>
                ) : (
                  <button
                    onClick={onStartDemo}
                    disabled={busy}
                    data-testid="micro-demo-resume"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-3 py-2.5"
                  >
                    <Play className="w-4 h-4" /> Resume
                  </button>
                )}
                <button
                  onClick={onClearDemo}
                  disabled={busy}
                  data-testid="micro-demo-clear"
                  className="px-3 py-2.5 border border-zinc-800 text-zinc-500"
                  aria-label="Clear demo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Toggle: Full terminal view */}
            <button
              onClick={() => setShowFull(v => !v)}
              data-testid="micro-toggle-full"
              className="w-full border border-zinc-900 hover:border-zinc-700 bg-zinc-950/40 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-center gap-2"
            >
              {showFull ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showFull ? "Hide details" : "Show details · hourly · live tail"}
            </button>

            {showFull && (
              <div className="space-y-3 pt-1" data-testid="micro-full-details">
                {/* Hourly bars — minimalist ascii-style */}
                <HourlyMini hourly={hourly} />

                {/* Recent transactions */}
                <div className="space-y-1.5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    // Recent
                  </div>
                  {(recent || []).slice(0, 8).map(t => {
                    const Icon = t.payment_type === "cash" ? Banknote : CreditCard;
                    return (
                      <div
                        key={t.id || t.external_id}
                        className="border border-zinc-900 bg-zinc-950/40 p-2.5 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Icon className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-mono text-[11px] text-white truncate">
                              {(t.items || []).map(i => i.name || i.sku).slice(0, 2).join(", ")
                                || PAYMENT_LABELS[t.payment_type] || t.payment_type || "Transaction"}
                            </div>
                            <div className="font-mono text-[9px] text-zinc-600">{fmtTime(t.occurred_at)}</div>
                          </div>
                        </div>
                        <div className={`font-heading font-black text-sm tracking-tighter flex-shrink-0 ${(t.total_cents || 0) < 0 ? "text-[#FF6B47]" : "text-[#FFB000]"}`}>
                          {fmtMoney(t.total_cents)}
                        </div>
                      </div>
                    );
                  })}
                  {(recent || []).length === 0 && (
                    <div className="text-center py-6 font-mono text-[10px] text-zinc-700">
                      No transactions yet.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="font-mono text-[9px] text-zinc-700 text-center pt-3">
              {streaming ? "Live · auto-refresh every 30s" : `Last update · ${fmtTime(today?.as_of)}`}
              {provider?.is_demo && " · DEMO DATA"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PulseCard({ line, full, onRefresh, refreshing }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = full && full.length > (line?.length || 0);
  return (
    <div className="border border-[#A78BFA]/30 bg-[#A78BFA]/[0.04] p-4" data-testid="micro-pulse">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A78BFA] flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> DARA Pulse
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="micro-pulse-refresh"
          className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#A78BFA] flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "…" : "Refresh"}
        </button>
      </div>
      <p className="text-sm text-zinc-100 leading-relaxed">
        {expanded && full ? full : line}
      </p>
      {hasMore && (
        <button
          onClick={() => setExpanded(v => !v)}
          data-testid="micro-pulse-expand"
          className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white mt-2"
        >
          {expanded ? "Less" : "More"}
        </button>
      )}
    </div>
  );
}

function HourlyMini({ hourly }) {
  const data = (hourly || []).map(h => ({
    label: hourLabel(h.hour),
    count: h.txn_count || 0,
    revenue: h.revenue_cents || 0,
  }));
  const max = Math.max(1, ...data.map(d => d.count));
  if (data.length === 0) return null;
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-4" data-testid="micro-hourly">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
        // Hourly today
      </div>
      <div className="flex items-end gap-1 h-24">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div
              className={`w-full ${d.count > 0 ? "bg-[#FFB000]" : "bg-zinc-900"}`}
              style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
              title={`${d.label}: ${d.count} txns`}
            />
            <div className="font-mono text-[8px] text-zinc-600 truncate w-full text-center">{d.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
